// eslint-disable react-hooks/exhaustive-deps
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  lookupUserIdsOrEmails,
  lookupWorkerTeams,
  lookupQualifications,
  lookupSquads,
  fetchUsersFromWT,
  fetchProjectQualifications,
} from '../api';
import { Checkbox, Icon, SegmentedButtons, Typography } from '@scale/scaleui';
import { IUserDetail, IListItem } from 'react-scale/components/WebinarSessions/types';
import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';
import SelectWorkerTeam from 'react-scale/corp/components/UI/SelectWorkerTeam';
import SelectQualification from 'react-scale/corp/components/UI/SelectQualification';
import SelectSquad from 'react-scale/corp/components/UI/SelectSquad';
import { SelectAudience } from './SelectAudience';
import { UserIdsOrEmailsTextInput } from './UserIdsOrEmailsTextInput';
import { IAudience } from '@scale/shared/types/audience';
import { useQuery } from 'react-query';

interface IInviteToolProps {
  invitedUsers: string[];
  setInvitedUsers: (ids: string[]) => void;
  audience: string | null;
  setAudience?: (id: string | null) => void;
  audienceBucket: number | null;
  setAudienceBucket?: (index: number | null) => void;
  invitedWorkerTeams: string[];
  setInvitedWorkerTeams: (ids: string[]) => void;
  invitedQualifications: string[];
  setInvitedQualifications: (ids: string[]) => void;
  invitedSquads: string[];
  setInvitedSquads: (ids: string[]) => void;
  projectId?: string;
  disabled?: boolean;
}

enum InvitedUsersInputMode {
  WorkerTeam = 'WT',
  UserIdsOrEmails = 'List',
  EmailSearch = 'Email',
  Qualification = 'Qualification',
  Squad = 'Squad',
  Audience = 'Audience',
}

export const InviteTool: React.FC<IInviteToolProps> = ({
  invitedUsers,
  invitedWorkerTeams,
  invitedQualifications,
  invitedSquads,
  audience,
  audienceBucket,
  setInvitedUsers,
  setInvitedWorkerTeams,
  setInvitedQualifications,
  setInvitedSquads,
  setAudience,
  setAudienceBucket,
  projectId,
  disabled,
}) => {
  const [inputMode, setInputMode] = useState<InvitedUsersInputMode>(
    InvitedUsersInputMode.WorkerTeam,
  );
  const [iUs, setIUs] = useState<IUserDetail[]>([]);
  const [iWTs, setIWTs] = useState<IListItem[]>([]);
  const [wtToUser, setWtToUser] = useState<Record<string, any[]>>({});
  const [iQs, setIQs] = useState<IListItem[]>([]);
  const [iSs, setISs] = useState<IListItem[]>([]);
  const [selectedAudience, setSelectedAudience] = useState<(IAudience & { id: string }) | null>(
    null,
  );

  const { data: projectQualifications, isLoading: loadingPQs } = useQuery(
    ['webinar-project-qualifications', projectId],
    () => fetchProjectQualifications(projectId ?? ''),
    {
      staleTime: 60 * 60 * 1000,
      enabled: !!projectId,
    },
  );

  const invitedUserIds = useMemo(() => {
    const ids = iUs.map(user => user.id);
    setInvitedUsers(ids);
    return ids;
  }, [iUs, setInvitedUsers]);
  const invitedWorkerTeamIds = useMemo(() => {
    const ids = iWTs.map(wt => wt.id);
    setInvitedWorkerTeams(ids);
    return ids;
  }, [iWTs, setInvitedWorkerTeams]);
  const invitedQualificationIds = useMemo(() => {
    const ids = iQs.map(qual => qual.id);
    setInvitedQualifications(ids);
    return ids;
  }, [iQs, setInvitedQualifications]);
  const invitedSquadIds = useMemo(() => {
    const ids = iSs.map(squad => squad.id);
    setInvitedSquads(ids);
    return ids;
  }, [iSs, setInvitedSquads]);

  const handleSelectUser = useCallback(
    (user: any) => {
      const userId = user.value;
      if (!invitedUserIds.includes(userId)) setIUs(h => [...h, { id: userId, email: user.label }]);
    },
    [invitedUserIds],
  );

  const handleSelectWorkerTeam = useCallback((wt: any) => {
    const workerTeamId = wt.value;
    if (!invitedWorkerTeamIds.includes(workerTeamId))
      (async () => {
        const users = await fetchUsersFromWT(workerTeamId);
        setIWTs((w: IListItem[]) => [...w, { name: wt.label, id: workerTeamId }]);
        setWtToUser((m: any) => ({ ...m, [workerTeamId]: users }));
      })();
  }, []);

  const handleSelectQualification = useCallback(
    (qual: any) => {
      const qualificationId = qual.value;
      if (!invitedQualificationIds.includes(qualificationId))
        setIQs(q => [...q, { id: qualificationId, name: qual.label }]);
    },
    [invitedQualificationIds],
  );

  const handleCheckQualification = useCallback(
    (qual: any, checked: boolean) => {
      const qualificationId = qual.id;
      if (checked && !invitedQualificationIds.includes(qualificationId)) setIQs(q => [...q, qual]);
      else if (!checked && invitedQualificationIds.includes(qualificationId))
        setIQs(qs => qs.filter(q => q.id !== qualificationId));
    },
    [invitedQualificationIds],
  );

  const handleSelectSquad = useCallback(
    (squad: any) => {
      const squadId = squad.value;
      if (!invitedSquadIds.includes(squadId))
        setISs(q => [...q, { id: squadId, name: squad.label }]);
    },
    [invitedSquadIds],
  );

  const removeInvitedUser = useCallback(
    (userId: string) => setIUs(u => u.filter(user => user.id !== userId)),
    [],
  );
  const removeInvitedWorkerTeam = useCallback(
    (teamId: string) => setIWTs(wt => wt.filter(t => t.id !== teamId)),
    [],
  );
  const removeInvitedQualification = useCallback(
    (qualificationId: string) => setIQs(q => q.filter(qual => qual.id !== qualificationId)),
    [],
  );
  const removeInvitedSquad = useCallback(
    (squadId: string) => setIQs(q => q.filter(squad => squad.id !== squadId)),
    [],
  );

  const removeAll = useCallback(() => {
    setIQs([]);
    setIWTs([]);
    setIUs([]);
  }, []);
  // TODO: move all this into a custom hook
  useEffect(() => {
    if (invitedUsers?.length)
      (async () => {
        const users = await lookupUserIdsOrEmails(invitedUsers);
        setIUs(users);
      })();
  }, []);

  useEffect(() => {
    if (invitedWorkerTeams?.length) {
      (async () => {
        const wts = await lookupWorkerTeams(invitedWorkerTeams);
        for (const wt of wts) {
          const users = await fetchUsersFromWT(wt.id);
          setWtToUser((m: any) => ({ ...m, [wt.id]: users }));
        }
        setIWTs(wts);
      })();
    }
  }, []);

  useEffect(() => {
    if (invitedQualifications?.length) {
      (async () => {
        const qs = await lookupQualifications(invitedQualifications);
        setIQs(qs);
      })();
    }
  }, []);

  useEffect(() => {
    if (invitedSquads?.length) {
      (async () => {
        const squads = await lookupSquads(invitedSquads);
        setISs(squads);
      })();
    }
  }, []);

  return (
    <>
      <Typography.Subtitle3 className="text-neutral-600 mb-1">Invitee(s)</Typography.Subtitle3>
      {!disabled && (
        <div className="flex flex-col gap-2 mb-1">
          <SegmentedButtons
            selected={inputMode}
            onSelect={(id: string) => setInputMode(id as InvitedUsersInputMode)}
            buttons={Object.values(InvitedUsersInputMode).map(m => ({ id: m, label: m }))}
            className="mb-1"
          />
          <Typography.Paragraph3 className="text-neutral-600">
            Note: specifying qualification(s) makes this session(s) mandatory and task-blocking
          </Typography.Paragraph3>
          {inputMode === InvitedUsersInputMode.Qualification && (
            <Typography.Paragraph3 className="text-neutral-600">
              Note: qualification will be automatically satisfied after webinar attendance
            </Typography.Paragraph3>
          )}
          {inputMode === InvitedUsersInputMode.UserIdsOrEmails && (
            <UserIdsOrEmailsTextInput setUsers={setIUs} />
          )}
          {inputMode === InvitedUsersInputMode.EmailSearch && (
            <SelectScaler
              placeholder="Search for a contributor by email"
              onChange={handleSelectUser}
              filterOption={() => true}
            />
          )}
          {inputMode === InvitedUsersInputMode.WorkerTeam && (
            <SelectWorkerTeam
              placeholder="Search for a WorkerTeam by name"
              value={null}
              onChange={handleSelectWorkerTeam}
            />
          )}
          {inputMode === InvitedUsersInputMode.Qualification && (
            <>
              {projectId ? (
                <>
                  {loadingPQs ? (
                    <Typography.Paragraph3>
                      Loading qualifications for this project...
                    </Typography.Paragraph3>
                  ) : (
                    <>
                      {projectQualifications?.length ? (
                        <>
                          {projectQualifications
                            .filter(pQ => !invitedQualificationIds.includes(pQ.id))
                            .map(pQ => (
                              <Checkbox
                                key={pQ.id}
                                label={pQ.name}
                                checked={invitedQualificationIds.includes(pQ.id)}
                                onChange={e => handleCheckQualification(pQ, e.target.checked)}
                              />
                            ))}
                        </>
                      ) : (
                        <Typography.Paragraph3>
                          No qualifications found for project.
                        </Typography.Paragraph3>
                      )}
                    </>
                  )}
                </>
              ) : (
                <SelectQualification
                  placeholder="Search for a qualification by name"
                  value={null}
                  onChange={handleSelectQualification}
                />
              )}
            </>
          )}
          {inputMode === InvitedUsersInputMode.Squad && (
            <SelectSquad
              placeholder="Search for a squad by name"
              value={null}
              onChange={handleSelectSquad}
            />
          )}
          {inputMode === InvitedUsersInputMode.Audience && (
            <SelectAudience
              audience={selectedAudience}
              setAudience={newAudience => {
                if (setAudience) {
                  setAudience(newAudience?._id ?? null);
                  setSelectedAudience(newAudience);
                }
              }}
              audienceBucket={audienceBucket}
              setAudienceBucket={setAudienceBucket}
            />
          )}
        </div>
      )}
      <div className="flex flex-col max-h-[108px] overflow-y-auto overflow-x-hidden relative">
        {iUs.length + iWTs.length + iQs.length >= 5 && !disabled && (
          <Icon
            size="sm"
            className="absolute right-0 top-0"
            icon={['far', 'trash']}
            onClick={() => removeAll()}
          />
        )}
        {audience && (
          <div key={audience} className="flex gap-1 items-center">
            <Typography.Paragraph3>
              • Audience: {audience}, Bucket: {(audienceBucket ?? 0) + 1}
            </Typography.Paragraph3>
          </div>
        )}
        {iWTs.map((wt: IListItem) => (
          <div key={wt.id} className="flex gap-1 items-center">
            <Typography.Paragraph3>
              • WT: {wt.name} ({wtToUser[wt.id]?.length ?? 0} users)
            </Typography.Paragraph3>
            {!disabled && (
              <Icon
                size="sm"
                icon={['far', 'trash']}
                onClick={() => removeInvitedWorkerTeam(wt.id)}
              />
            )}
          </div>
        ))}
        {iQs.map((qualification: IListItem) => (
          <div key={qualification.id} className="flex gap-1 items-center">
            <Typography.Paragraph3>• Qualification: {qualification.name}</Typography.Paragraph3>
            {!disabled && (
              <Icon
                size="sm"
                icon={['far', 'trash']}
                onClick={() => removeInvitedQualification(qualification.id)}
              />
            )}
          </div>
        ))}
        {iSs.map((squad: IListItem) => (
          <div key={squad.id} className="flex gap-1 items-center">
            <Typography.Paragraph3>• Squad: {squad.name}</Typography.Paragraph3>
            {!disabled && (
              <Icon
                size="sm"
                icon={['far', 'trash']}
                onClick={() => removeInvitedSquad(squad.id)}
              />
            )}
          </div>
        ))}
        {iUs.map((user: IUserDetail) => (
          <div key={user.id} className="flex gap-1 items-center">
            <Typography.Paragraph3>
              • {user.email}
              {user.name ? ` (${user.name})` : ''}
            </Typography.Paragraph3>
            {!disabled && (
              <Icon size="sm" icon={['far', 'trash']} onClick={() => removeInvitedUser(user.id)} />
            )}
          </div>
        ))}
      </div>
    </>
  );
};
